/* ==========================================================================
   NaturoBest Landing Page System — nb-landing.js
   --------------------------------------------------------------------------
   Deliberately dependency-free. Add-to-cart itself is handled by the theme's
   own <product-form> custom element (assets/theme-global.js), so nothing here
   touches the cart. This file only does presentation:

     1. Sticky buy bar reveal
     2. Variant toggle (updates [name=id] + the displayed price)
     3. Anchor nav — active state, and scrolling without a URL hash
     4. Optional suppression of the theme's mobile sticky bar
     5. FAQ accordion (one open at a time, animated)

   Re-runs on Shopify theme editor section load/unload.
   ========================================================================== */

(function () {
  'use strict';

  /* 1. Sticky buy bar ---------------------------------------------------- */

  function initStickyBar(root) {
    var bar = root.querySelector('[data-nb-sticky-bar]');
    if (!bar || bar.dataset.nbReady === 'true') return;
    bar.dataset.nbReady = 'true';

    if (bar.dataset.nbHideThemeMobileBar === 'true') {
      document.body.classList.add('nb-hide-theme-mobile-bar');
    }

    var root_ = document.documentElement;
    var spacer = document.querySelector('[data-nb-sticky-spacer]');
    var alwaysOn = bar.dataset.nbReveal === 'always';
    var trigger = document.querySelector(bar.dataset.nbTrigger || '[data-nb-sticky-trigger]');
    var fallbackOffset = parseInt(bar.dataset.nbOffset, 10) || 600;

    // The theme header's own height variable (--m-header-height) covers the
    // header element only — it excludes the announcement bar and the topbar.
    // Measuring the wrapper's live bottom edge instead handles the
    // announcement bar, the sticky/non-sticky states and the header's
    // hide-on-scroll-down behaviour in one go.
    var themeHeader =
      document.querySelector('.m-header__wrapper') || document.querySelector('.m-header');
    // The wrapper is what moves; the state class lives on its parent.
    var themeHeaderRoot = document.querySelector('.m-header');

    function headerBottom() {
      if (!themeHeader) return 0;
      var rect = themeHeader.getBoundingClientRect();
      return rect.bottom > 0 ? Math.round(rect.bottom) : 0;
    }

    function isMobile() {
      return window.matchMedia('(max-width: 980px)').matches;
    }

    var ticking = false;
    var followUntil = 0;
    var lastOffset = null;
    var lastStickyH = null;
    var lastSpacer = null;
    var lastStuck = null;
    var settleTimer = null;

    /* The theme flips .m-header to .stuck as it becomes sticky, and that rule
       changes the wrapper from position:relative to position:fixed. A position
       change cannot animate, so the header's bottom edge — and with it this
       bar's top — moves in a single step.

       Easing is switched on only for the length of that switch. Leaving a
       transition on top permanently would be worse: top is resampled every
       frame while scrolling, so the bar would trail the header the whole way
       down instead of sitting under it. */
    function settle() {
      bar.classList.add('is-settling');

      /* Force a style flush before the caller writes the new top.

         A transition needs the property to be present in the element's style
         BEFORE the value changes. Adding the class and moving top inside the
         same frame lands both in one recalc, so the browser sees no "before"
         state to animate from and jumps anyway — which is why the first version
         of this only took the edge off. Reading a layout property commits the
         class first. */
      void bar.offsetWidth;

      clearTimeout(settleTimer);
      settleTimer = setTimeout(function () {
        bar.classList.remove('is-settling');
      }, 420);
    }

    function update() {
      var mobile = isMobile();

      var stuck = themeHeaderRoot ? themeHeaderRoot.classList.contains('stuck') : false;
      if (lastStuck !== null && stuck !== lastStuck) settle();
      lastStuck = stuck;

      // Position: below whatever the theme header currently occupies.
      var offset = mobile ? '0px' : headerBottom() + 'px';
      if (offset !== lastOffset) {
        root_.style.setProperty('--nb-header-offset', offset);
        lastOffset = offset;
      }

      // Visibility.
      var visible;
      if (alwaysOn) {
        visible = true;
      } else if (trigger) {
        visible = trigger.getBoundingClientRect().bottom <= headerBottom();
      } else {
        visible = window.scrollY > fallbackOffset;
      }
      bar.classList[visible ? 'add' : 'remove']('is-visible');

      // Reserve layout space only while the bar is permanently on screen.
      var height = bar.offsetHeight;
      var spacerH = alwaysOn && !mobile ? height + 'px' : '0px';
      if (spacer && spacerH !== lastSpacer) {
        spacer.style.height = spacerH;
        lastSpacer = spacerH;
      }

      // Anchor targets must clear the bar on desktop; on mobile it is docked
      // to the bottom of the viewport so no extra offset is needed.
      var stickyH = mobile ? '0px' : height + 'px';
      if (stickyH !== lastStickyH) {
        root_.style.setProperty('--nb-sticky-h', stickyH);
        lastStickyH = stickyH;
      }
    }

    /* Keep sampling for a beat after the last scroll event.

       The theme header slides itself in and out over half a second
       (.m-header__wrapper transitions transform, --m-duration-long). Scroll
       events stop the moment the reader does, so sampling on scroll alone left
       this bar frozen part-way through that slide — and it then snapped into
       place on the next scroll, which is the jump you see coming back to the
       top. Following for 600ms covers the header's whole animation.

       The writes above are all guarded on change, so the extra frames cost a
       couple of layout reads and nothing else. */
    function follow() {
      update();
      if (Date.now() < followUntil) {
        window.requestAnimationFrame(follow);
        return;
      }
      ticking = false;
    }

    function schedule() {
      followUntil = Date.now() + 600;
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(follow);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    update();
    // The theme sets its header height asynchronously, so re-measure once
    // everything has settled.
    window.addEventListener('load', update);
    setTimeout(update, 300);
  }

  /* 2. Variant toggle ---------------------------------------------------- */

  function initVariantToggle(root) {
    var groups = root.querySelectorAll('[data-nb-variants]');

    Array.prototype.forEach.call(groups, function (group) {
      if (group.dataset.nbReady === 'true') return;
      group.dataset.nbReady = 'true';

      var form = group.closest('form');
      if (!form) return;

      var idInput = form.querySelector('[name="id"]');
      // The price node may live outside the form (e.g. beside it in the hero).
      var scope = group.closest('[data-nb-buy]') || form;
      var priceNode = scope.querySelector('[data-nb-price]');
      var submitButton = form.querySelector('[name="add"]');

      group.addEventListener('change', function (event) {
        var input = event.target;
        if (!input || input.type !== 'radio') return;

        if (idInput) idInput.value = input.value;

        if (priceNode && input.dataset.nbPrice) {
          priceNode.innerHTML = input.dataset.nbPrice;
        }

        if (submitButton) {
          var soldOut = input.dataset.nbAvailable === 'false';
          submitButton.disabled = soldOut;
          submitButton.textContent = soldOut
            ? submitButton.dataset.nbSoldOutLabel || 'Sold out'
            : submitButton.dataset.nbLabel || submitButton.textContent;
        }
      });
    });
  }

  /* 3. Anchor nav active state ------------------------------------------- */

  function initAnchorNav(root) {
    var nav = root.querySelector('[data-nb-nav]');
    if (!nav || nav.dataset.nbReady === 'true') return;
    nav.dataset.nbReady = 'true';

    var links = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
    if (!links.length) return;

    var targets = links
      .map(function (link) {
        var id = link.getAttribute('href').slice(1);
        var el = id ? document.getElementById(id) : null;
        return el ? { link: link, el: el } : null;
      })
      .filter(Boolean);

    if (!targets.length) return;

    /* Scroll without leaving a fragment in the address bar.

       The markup stays a real <a href="#...">, so the links are still keyboard
       reachable, still open in a new tab on modified click, and still work with
       JavaScript off. This only takes over the plain left click.

       Why bother: every click used to write a hash, and the browser restores
       that hash on the next refresh — so reloading the page jumped the reader
       back to whichever section they last clicked. scroll-margin-top on the
       target still applies, so the heading clears the header and the bar. */
    links.forEach(function (link) {
      link.addEventListener('click', function (event) {
        if (event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        var match = targets.filter(function (t) { return t.link === link; })[0];
        if (!match) return;

        var reduce =
          !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

        event.preventDefault();
        match.el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });

        if (window.history && history.replaceState) {
          history.replaceState(null, '', location.pathname + location.search);
        }
      });
    });

    if (!('IntersectionObserver' in window)) return;

    /* Track which sections are in view, rather than reacting to the last one
       that entered. The previous version only ever ADDED the active class, so
       anywhere no target was intersecting — above the first one, in the hero —
       whatever had been lit last stayed lit. */
    var inView = [];

    function paint() {
      var current = null;

      // Topmost section in view wins, so scrolling back up hands the highlight
      // over in the order the reader actually sees them.
      targets.forEach(function (t) {
        if (inView.indexOf(t.el) === -1) return;
        if (!current || t.el.getBoundingClientRect().top < current.el.getBoundingClientRect().top) {
          current = t;
        }
      });

      links.forEach(function (l) {
        l.classList[current && l === current.link ? 'add' : 'remove']('is-active');
      });
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var i = inView.indexOf(entry.target);
          if (entry.isIntersecting && i === -1) inView.push(entry.target);
          if (!entry.isIntersecting && i !== -1) inView.splice(i, 1);
        });
        paint();
      },
      // Activate a link when its section crosses the upper third of the screen.
      { rootMargin: '-25% 0px -65% 0px', threshold: 0 }
    );

    targets.forEach(function (t) { observer.observe(t.el); });
  }

  /* 4. Video facade ------------------------------------------------------- */
  // The player is only injected on click, so the embed costs nothing on load.

  function initVideo(root) {
    var players = root.querySelectorAll('[data-nb-video]');

    Array.prototype.forEach.call(players, function (player) {
      if (player.dataset.nbReady === 'true') return;
      player.dataset.nbReady = 'true';

      var poster = player.querySelector('[data-nb-video-poster]');
      var embed = player.dataset.nbEmbed;
      if (!poster || !embed) return;

      poster.addEventListener('click', function () {
        var iframe = document.createElement('iframe');
        iframe.src = embed;
        iframe.title = poster.getAttribute('aria-label') || 'Product video';
        iframe.allow =
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.setAttribute('allowfullscreen', '');
        iframe.loading = 'lazy';
        player.innerHTML = '';
        player.appendChild(iframe);
      });
    });
  }

  /* 5. FAQ accordion ------------------------------------------------------ */

  /* <details> has no height to animate between — it jumps from summary-only to
     full. So the click is intercepted and the height is driven here, which also
     makes closing a sibling a real transition rather than a snap.

     The markup still carries the native exclusive name attribute. That is the
     no-JavaScript path; once this takes over it drops the attribute, otherwise
     the browser would slam the siblings shut mid-animation. */

  var FAQ_DURATION = 260;
  var FAQ_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';

  function faqReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Height with the answer hidden: the summary plus the item's own borders,
     which offsetHeight counts and clientHeight does not. */
  function faqClosedHeight(item, summary) {
    return summary.offsetHeight + item.offsetHeight - item.clientHeight;
  }

  function faqStop(item) {
    if (!item.nbAnimation) return;
    item.nbAnimation.cancel();
    item.nbAnimation = null;
  }

  function faqSlide(item, from, to, onDone) {
    item.style.overflow = 'hidden';

    var animation = item.animate(
      { height: [from + 'px', to + 'px'] },
      { duration: FAQ_DURATION, easing: FAQ_EASING }
    );
    item.nbAnimation = animation;

    animation.onfinish = function () {
      item.nbAnimation = null;
      item.style.overflow = '';
      if (onDone) onDone();
    };
  }

  function faqClose(item, summary) {
    // Read before stopping: cancelling drops the animated height.
    var from = item.offsetHeight;
    faqStop(item);

    if (!summary || faqReducedMotion()) {
      item.removeAttribute('data-nb-closing');
      item.open = false;
      return;
    }

    // Drives the + back round now rather than when the slide lands.
    item.setAttribute('data-nb-closing', '');

    faqSlide(item, from, faqClosedHeight(item, summary), function () {
      item.removeAttribute('data-nb-closing');
      item.open = false;
    });
  }

  function faqOpen(item, list) {
    var others = list.querySelectorAll('details[open]');
    Array.prototype.forEach.call(others, function (other) {
      if (other !== item && !other.hasAttribute('data-nb-closing')) {
        faqClose(other, other.querySelector('summary'));
      }
    });

    var from = item.offsetHeight;
    faqStop(item);
    item.removeAttribute('data-nb-closing');
    item.open = true;

    if (faqReducedMotion()) return;
    faqSlide(item, from, item.offsetHeight);
  }

  function initFaq(root) {
    var lists = root.querySelectorAll('[data-nb-faq]');
    if (!lists.length || typeof Element.prototype.animate !== 'function') return;

    Array.prototype.forEach.call(lists, function (list) {
      if (list.dataset.nbReady === 'true') return;
      list.dataset.nbReady = 'true';

      var items = list.querySelectorAll('details');

      Array.prototype.forEach.call(items, function (item) {
        var summary = item.querySelector('summary');
        if (!summary) return;

        item.removeAttribute('name');

        summary.addEventListener('click', function (event) {
          event.preventDefault();

          if (item.open && !item.hasAttribute('data-nb-closing')) {
            faqClose(item, summary);
          } else {
            faqOpen(item, list);
          }
        });
      });
    });
  }

  /* Boot ----------------------------------------------------------------- */

  function init(root) {
    var scope = root || document;
    initStickyBar(scope);
    initVariantToggle(scope);
    initAnchorNav(scope);
    initVideo(scope);
    initFaq(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor support.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target.querySelector('[data-nb-sticky-bar]')) {
      document.body.classList.remove('nb-hide-theme-mobile-bar');
    }
  });
})();
